// THE SHOT GEOMETRY — where a round actually leaves from, and along what line.
//
// This is two small rules that used to live inline in src/player/controller.js
// fireShot(), lifted out for one reason: they decide where EVERY bullet in the
// game goes, and controller.js imports Babylon, so nothing in that file can be
// run in plain Node. src/world/query.mjs, src/world/melee-arc.mjs and
// src/world/infected-sim.mjs are .mjs for exactly this reason — it is the only
// verification loop this project has (no browser in the loop) — and the
// convergence below is the single change in this workstream with the widest
// blast radius, so it belongs on the provable side of that line. Babylon-free
// and import-free; points and directions are plain {x, y, z}, which a Babylon
// Vector3 already satisfies. Proven by tools/shottest.mjs.
//
// It is .mjs and not .js on purpose: package.json has no "type": "module", so
// a .js file here loads as CommonJS under Node and the harness cannot see it.

// A camera ray landing closer than this to the muzzle cannot aim it: the
// convergence direction would swing wildly or point backwards (the lens
// clipped into a wall, or the character's own body right under the barrel).
export const AIM_MIN_CONVERGE = 1.5;

// How far a collider containing the MUZZLE may be walked out of before the
// round is treated as genuinely buried rather than sitting in a movement pad.
// Two numbers, because one cannot tell the two cases apart:
//
//   • A prop or a door is a loose box around a thin object — props.js addCol
//     wraps a 0.12 m fence slab in a 0.80 m box and a sedan's visible flank in
//     a 2.44 m one — so a muzzle inside one is a pad, essentially always, and
//     walking out to the far face is right even when that face is metres away.
//     Measured: a sedan's across-axis exit from inside reaches 2.44 m, which a
//     single 2 m cap refused — the exact case the fix exists for.
//   • Anything else (a building OBB, a trunk, a body) is solid through its
//     whole volume, so only a SHORT exit can be a pad or a wall. 1.0 m covers
//     buildings.js's 0.35 m sealed pad, its 0.6 m enterable walls, its 0.24 m
//     interior partitions and its +0.1 m furniture boxes, while a muzzle
//     genuinely buried in a sealed building has an exit of metres and keeps
//     the dist-0 answer, which for solid geometry is the right one.
export const INSIDE_EXIT_PAD = 5.0;
export const INSIDE_EXIT_SOLID = 1.0;

/**
 * The direction a round should leave the MUZZLE along, given the camera the
 * crosshair belongs to.
 *
 * WHY IT IS TWO RAYS. The crosshair is the centre of the CAMERA, which in third
 * person sits ~3.4 m behind and 0.45 m to the right of the character (camera.js
 * dist / shoulder) and in ADS 1.5 m behind and 0.76 m off the shoulder. Tracing
 * the bullet from there is how a shot "around a corner" came out on the far
 * side of the wall: the camera had already cleared the corner the character was
 * hiding behind. But tracing from the muzzle along the raw camera DIRECTION is
 * not the fix either — the two lines are then parallel, displaced by the whole
 * perpendicular part of camera→muzzle, for ever and at every range.
 *
 * So the pair converges: the CAMERA ray decides WHAT the player is aiming at
 * (that is the only line the crosshair means), and the round is the MUZZLE ray
 * aimed at that point. Both lines pass through the aim point, so the crosshair
 * stays honest at range, while occlusion is judged from where the gun is.
 *
 * THE DEAD BAND, and why it no longer gives up. An aim point within
 * AIM_MIN_CONVERGE of the muzzle cannot steer it — the muzzle→point vector is
 * then mostly the camera-to-muzzle offset and the round would leave sideways.
 * Falling back to the raw camera direction there was worse, not safer: the
 * parallel displacement is ~0.33 m from the hip and ~0.6 m in ADS, against the
 * 0.40 m body cylinder main.js publishes, so it was a clean MISS — and the band
 * covers 0 to 1.5 m, which in a DayZ-like is every door breach, every execution
 * and every infected already on top of you. Measured with the real ADS numbers
 * (tools/shottest.mjs): every target from 0.35 m to 1.35 m in front of the
 * barrel was missed outright. Hip fire escaped only by accident — its offset
 * is 0.394 m against the 0.40 m body, so the parallel line grazed the cylinder
 * by six millimetres.
 *
 * The fix keeps the aim point ON the camera line and only pushes it OUT, to
 * AIM_MIN_CONVERGE past the muzzle's own station along that line. The direction
 * is then always stable and always forward, and the round's miss distance ramps
 * from the old constant 0.6 m down to zero across the band instead of sitting
 * at 0.6 m all the way in.
 *
 * @param camPos  {x,y,z} lens position
 * @param aimDir  {x,y,z} NORMALISED camera forward (the crosshair line)
 * @param muzzle  {x,y,z} barrel end
 * @param look    the query's answer for the camera ray, or null — only `dist`
 *                and `inside` are read, so a caller may pass anything with
 *                those. `inside` (lens clipped into geometry) and a hit right
 *                on the lens are not aim points.
 * @param lead    how far the camera ray was traced (metres)
 * @returns {x,y,z} the direction to fire, or NULL when there is no usable
 *          convergence and the caller should keep its own aimDir. NULL rather
 *          than a copy of aimDir so nothing is allocated for that case.
 *          Because the aim point is CLAMPED to AIM_MIN_CONVERGE past the
 *          muzzle, the convergence is now always both forward and stable for
 *          finite input, so null means a non-finite camera, direction or
 *          muzzle — a NaN fails the `tl > AIM_MIN_CONVERGE` test and the round
 *          leaves along the crosshair instead of along NaN.
 *
 * WHAT IT STILL CANNOT DO. A single line from the muzzle cannot pass through
 * the crosshair at EVERY range: the residual miss at distance d is
 * offset * (1 - d / conv), so with the ADS offset of 0.51 m and a 1.5 m
 * convergence the round is still outside a 0.40 m body inside ~0.32 m of the
 * barrel. Shortening `conv` only moves the error to the far side of the band.
 * Closing that last third of a metre needs the round to LEAVE from a point on
 * the camera line (a virtual muzzle) rather than from the barrel, which is a
 * separate change with its own visual consequences — see the handoff note.
 */
export function convergeAim(camPos, aimDir, muzzle, look, lead) {
  const at = look && !look.inside && look.dist > AIM_MIN_CONVERGE ? look.dist : lead;
  // how far along the camera ray the muzzle itself stands
  const ahead = (muzzle.x - camPos.x) * aimDir.x
    + (muzzle.y - camPos.y) * aimDir.y + (muzzle.z - camPos.z) * aimDir.z;
  const conv = at > ahead + AIM_MIN_CONVERGE ? at : ahead + AIM_MIN_CONVERGE;
  const tx = camPos.x + aimDir.x * conv - muzzle.x;
  const ty = camPos.y + aimDir.y * conv - muzzle.y;
  const tz = camPos.z + aimDir.z * conv - muzzle.z;
  const tl = Math.hypot(tx, ty, tz);
  if (!(tl > AIM_MIN_CONVERGE) || tx * aimDir.x + ty * aimDir.y + tz * aimDir.z <= 0) return null;
  return { x: tx / tl, y: ty / tl, z: tz / tl };
}

/**
 * A MUZZLE INSIDE A COLLIDER IS NOT A ROUND VANISHING AT THE BARREL.
 *
 * It happens constantly, because the colliders the query reads are the MOVEMENT
 * ones: props.js addCol() stores every prop at (hw + 0.3, hd + 0.3) so pushOut
 * can keep a body's radius clear of it, and pushOut then parks the player's
 * CENTRE exactly on that skin's face — which puts the barrel end, 0.4-0.6 m
 * further forward and inside the prop's y band, inside the collider every time
 * you take cover behind a car, a fence or a crate. query.ray answers `inside`,
 * dist 0, and the caller then draws no tracer, replicates no shot, fires the
 * impact at the player's own feet and deals no damage: the round is deleted.
 *
 * The honest answer is neither "nothing happened" nor "the round flies on
 * regardless": it is that the round stops at the FAR FACE of whatever the
 * muzzle is inside. So the hit keeps its collider, its surface and its normal
 * and moves out to `exit` — the tracer has a length, the impact lands on
 * geometry, other players see the shot, and nothing beyond the collider can be
 * reached, because the caller clamps its mesh pick to this same distance.
 *
 * Capped so it can never become shoot-through — see INSIDE_EXIT_PAD /
 * INSIDE_EXIT_SOLID for which cap applies to what, and why there are two.
 *
 * @param hit     a query ray result, or null
 * @param muzzle  {x,y,z} the ray origin
 * @param dir     {x,y,z} NORMALISED ray direction
 * @returns the hit to use — `hit` itself when nothing needs changing, or a new
 *          object moved out to the far face.
 */
export function resolveInside(hit, muzzle, dir) {
  if (!hit || !hit.inside) return hit;
  const e = hit.exit;
  const cap = hit.kind === 'prop' || hit.kind === 'door' ? INSIDE_EXIT_PAD : INSIDE_EXIT_SOLID;
  if (!(e > 0) || e > cap) return hit;
  return {
    ...hit,
    dist: e,
    inside: false,
    point: { x: muzzle.x + dir.x * e, y: muzzle.y + dir.y * e, z: muzzle.z + dir.z * e },
  };
}
